export type DebouncedFunction<T extends (...args: any[]) => void> = {
	(...args: Parameters<T>): void;
	cancel: () => void;
};

/**
 * Simple debounce helper for browser environments.
 */
export function debounce<T extends (...args: any[]) => void>(
	fn: T,
	wait = 100,
	immediate = false
): DebouncedFunction<T> {
	let timeoutId: number | null = null;

	const debounced = function (this: ThisParameterType<T>, ...args: Parameters<T>) {
		const callNow = immediate && timeoutId === null;

		if (timeoutId !== null) {
			window.clearTimeout(timeoutId);
		}

		timeoutId = window.setTimeout(() => {
			timeoutId = null;
			if (!immediate) {
				fn.apply(this, args);
			}
		}, wait);

		if (callNow) {
			fn.apply(this, args);
		}
	};

	debounced.cancel = () => {
		if (timeoutId !== null) {
			window.clearTimeout(timeoutId);
			timeoutId = null;
		}
	};

	return debounced;
}

/**
 * Invoke a callback once the document is ready.
 */
export function onDocumentLoaded(callback: () => void, waitForWindowLoad = false): void {
	const readyState = document.readyState;
	const isReady = readyState === 'complete' || (!waitForWindowLoad && readyState === 'interactive');

	if (isReady) {
		callback();
		return;
	}

	const eventTarget = waitForWindowLoad ? window : document;
	const eventName = waitForWindowLoad ? 'load' : 'DOMContentLoaded';

	const handler = () => {
		eventTarget.removeEventListener(eventName, handler);
		callback();
	};

	eventTarget.addEventListener(eventName, handler);
}

